import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Row,
  Modal,
  Form,
  FormGroup,
  Col,
  Card,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";
import ClipLoader from "react-spinners/ClipLoader";
import { TagTransactionRule } from "src/docuclipper/DocuclipperTypes";
import TagRegexesTransactionViewOnly from "./TagRegexesTransactionViewOnly";
import {
  addNewTransactionGroup,
  deleteTagRegexesTransactionAction,
  fetchTagRegexesTransaction,
  updateTagRegexesTransactionAction,
} from "src/redux/reducers/TagRegexesTransaction";
import { ReduxState } from "src/redux";
import { LoadingButton } from "src/views/Docuclipper/TrackedButton";
import ErrorMessage from "src/views/Docuclipper/ErrorMessage";

type Props = {
  showModal: boolean;
  onToggleModal: () => void;
  integrationId?: number | null;
};

type Rule = {
  ifConditions: {
    field: string;
    operator: string;
    value: { keywords?: string[]; amount?: number };
  }[];
  thenSet: { actionType: string; value: string }[];
  createdAt: string;
};

const defaultErrorMap = {
  keyword: false,
  tag: false,
};

export const TagRegexesTransaction: React.FC<Props> = ({
  showModal,
  onToggleModal,
  integrationId = null,
}) => {
  const dispatch = useDispatch();
  const [combinationOperator, setCombinationOperator] = useState("all");
  const [rules, setRules] = useState<Rule[]>([]);
  const [editRuleIndex, setEditRuleIndex] = useState<number | null>(null);
  const [addingNewRule, setAddingNewRule] = useState(false);
  const [errorMap, setErrorMap] = useState<{ [key: string]: boolean }>({
    ...defaultErrorMap,
  });

  const { loading, tagRegexesTransaction } = useSelector(
    (state: ReduxState) => state.TagRegexesTransaction
  );

  // Available fields for transaction rules
  const availableFields = [
    { value: "description", label: "Description" },
    { value: "amount", label: "Amount" },
    { value: "date", label: "Date" },
    { value: "balance", label: "Balance" },
    { value: "account", label: "Account" },
  ];

  // Available operators for different field types
  const getOperatorsForField = (field: string) => {
    if (field === "amount" || field === "balance") {
      return [
        { value: "greaterThan", label: "Greater Than" },
        { value: "lessThan", label: "Less Than" },
        { value: "greaterThanOrEqual", label: "Greater Than or Equal" },
        { value: "lessThanOrEqual", label: "Less Than or Equal" },
        { value: "equal", label: "Equals" },
        { value: "notEqual", label: "Not Equals" },
      ];
    } else {
      return [
        { value: "containsAny", label: "Contains" },
        { value: "doesNotContainAny", label: "Not Contains" },
        { value: "equal", label: "Equals" },
        { value: "notEqual", label: "Not Equals" },
      ];
    }
  };

  const toggle = () => {
    onToggleModal();
    setEditRuleIndex(null);
    setErrorMap({ ...defaultErrorMap });
  };

  const groupName = `Transaction Rules | ${
    integrationId ? `Integration ${integrationId}` : "General"
  }`;

  const ruleToEdit = editRuleIndex !== null ? rules[editRuleIndex] : null;

  const selectedFields = ruleToEdit
    ? ruleToEdit.ifConditions.map((condition) => condition.field)
    : [];
  const remainingFields = availableFields.filter(
    (field) => !selectedFields.includes(field.value)
  );

  const hasError = Object.values(errorMap).some((value) => value);

  const handleRuleChange = (ruleIndex, index, field, value) => {
    const newRules = [...rules];
    if (field === "actionType") {
      newRules[ruleIndex].thenSet[index][field] = value;
    } else {
      newRules[ruleIndex].thenSet[index].value = value;
    }
    setRules(newRules);
  };

  const handleIfConditionChange = (ruleIndex, index, field, value) => {
    const newRules = [...rules];
    if (field === "operator") {
      newRules[ruleIndex].ifConditions[index][field] = value;
      // Reset value when operator changes
      const fieldType = newRules[ruleIndex].ifConditions[index].field;
      if (fieldType === "amount" || fieldType === "balance") {
        newRules[ruleIndex].ifConditions[index].value = { amount: 0 };
      } else {
        newRules[ruleIndex].ifConditions[index].value = { keywords: [""] };
      }
    } else if (field === "field") {
      newRules[ruleIndex].ifConditions[index][field] = value;
      // Reset operator and value when field changes
      newRules[ruleIndex].ifConditions[index].operator =
        value === "amount" || value === "balance"
          ? "greaterThan"
          : "containsAny";
      if (value === "amount" || value === "balance") {
        newRules[ruleIndex].ifConditions[index].value = { amount: 0 };
      } else {
        newRules[ruleIndex].ifConditions[index].value = { keywords: [""] };
      }
    } else if (field === "value") {
      const fieldType = newRules[ruleIndex].ifConditions[index].field;
      if (fieldType === "amount" || fieldType === "balance") {
        newRules[ruleIndex].ifConditions[index].value = {
          amount: parseFloat(value) || 0,
        };
      } else {
        if (typeof value === "string") {
          newRules[ruleIndex].ifConditions[index].value = {
            keywords: value.split(",").map((keyword) => keyword.trim()),
          };
        } else {
          newRules[ruleIndex].ifConditions[index].value = value;
        }
      }
    } else {
      newRules[ruleIndex].ifConditions[index][field] = value;
    }
    setRules(newRules);
  };

  const handleOnCreateRule = () => {
    const lastRule = rules[rules.length - 1];
    const firstCondition = lastRule.ifConditions[0];

    // Validate condition value
    const fieldType = firstCondition.field;
    if (fieldType === "amount" || fieldType === "balance") {
      if (!firstCondition.value.amount && firstCondition.value.amount !== 0) {
        setErrorMap({ ...errorMap, keyword: true });
        return;
      }
    } else {
      if (
        !firstCondition.value.keywords ||
        firstCondition.value.keywords[0] === ""
      ) {
        setErrorMap({ ...errorMap, keyword: true });
        return;
      }
    }

    if (lastRule.thenSet[0].value === "") {
      setErrorMap({ ...errorMap, tag: true });
      return;
    }

    setErrorMap({ ...errorMap, keyword: false, tag: false });

    const newRule = lastRule;
    const tag: TagTransactionRule = {
      tag: `${groupName} - Rule ${rules.length}`,
      jsonRules: JSON.stringify({
        conditions: {
          [combinationOperator]: newRule.ifConditions.map((condition) => ({
            fact: condition.field,
            operator: condition.operator,
            value: condition.value,
          })),
        },
        event: {
          type: "transactionRules",
          params: {
            tag: newRule.thenSet[0].value,
          },
        },
      }),
    };
    dispatch(addNewTransactionGroup(groupName, tag, integrationId));
    setAddingNewRule(false);
    setEditRuleIndex(null);
  };

  const handleOnBlurUpdateRule = (ruleIndex) => {
    if (addingNewRule) return;

    const ruleToUpdate = rules[ruleIndex];
    const tagRegex = tagRegexesTransaction[ruleIndex];

    if (!tagRegex?.id) return;

    const jsonRule = {
      conditions: {
        [combinationOperator]: ruleToUpdate.ifConditions.map((condition) => ({
          fact: condition.field,
          operator: condition.operator,
          value: condition.value,
        })),
      },
      event: {
        type: "transactionRules",
        params: {
          tag: ruleToUpdate.thenSet[0].value,
        },
      },
    };

    dispatch(
      updateTagRegexesTransactionAction(tagRegex.id, jsonRule, integrationId)
    );
  };

  const handleClearAddRule = () => {
    setAddingNewRule(false);
    setEditRuleIndex(null);
    setErrorMap({ ...defaultErrorMap });
  };

  const handleOnRemoveRule = (ruleIndex) => {
    if (addingNewRule && ruleIndex === rules.length - 1) {
      setRules(rules.filter((_, index) => index !== ruleIndex));
      handleClearAddRule();
    } else {
      const ruleToRemove = tagRegexesTransaction[ruleIndex];
      if (ruleToRemove) {
        dispatch(
          deleteTagRegexesTransactionAction(ruleToRemove.id, integrationId)
        );
        setRules(rules.filter((_, index) => index !== ruleIndex));
        handleClearAddRule();
      }
    }
  };

  const handleAddRule = () => {
    const newRule = {
      ifConditions: [
        {
          field: "description",
          operator: "containsAny",
          value: { keywords: [""] },
        },
      ],
      thenSet: [{ actionType: "tag", value: "" }],
      createdAt: new Date().toISOString(),
    };
    const newRules = [...rules, newRule];
    setRules(newRules);
    setEditRuleIndex(newRules.length - 1);
    setAddingNewRule(true);
  };

  const handleAddThenSet = (ruleIndex) => {
    const newRules = [...rules];
    newRules[ruleIndex].thenSet.push({ actionType: "tag", value: "" });
    setRules(newRules);
  };

  const handleAddIfCondition = (ruleIndex) => {
    const newRules = [...rules];
    newRules[ruleIndex].ifConditions.push({
      field: "description",
      operator: "containsAny",
      value: { keywords: [""] },
    });
    setRules(newRules);
  };

  const handleRemoveThenSet = (ruleIndex, index) => {
    if (rules[ruleIndex].thenSet.length > 1) {
      const newRules = [...rules];
      newRules[ruleIndex].thenSet.splice(index, 1);
      setRules(newRules);
    }
  };

  const handleRemoveIfCondition = (ruleIndex, index) => {
    if (rules[ruleIndex].ifConditions.length > 1) {
      const newRules = [...rules];
      newRules[ruleIndex].ifConditions.splice(index, 1);
      setRules(newRules);
    }
  };

  useEffect(() => {
    if (showModal) {
      dispatch(fetchTagRegexesTransaction("transactionRules", integrationId));
    }
  }, [showModal]);

  useEffect(() => {
    const existingRules = tagRegexesTransaction.map((tagRegex) => {
      const baseRule = {
        conditions: {
          all: [
            {
              fact: "description",
              operator: "containsAny",
              value: { keywords: [""] },
            },
          ],
        },
        event: {
          type: "transactionRules",
          params: {
            tag: "",
          },
        },
      };

      const parsedRules = (tagRegex?.jsonRules as any) || baseRule;
      const operator = parsedRules.conditions?.all ? "all" : "any";

      return {
        ifConditions: parsedRules.conditions[operator]?.map((condition) => ({
          field: condition.fact,
          operator: condition.operator,
          value: condition.value,
        })),
        thenSet: [
          {
            actionType: "tag",
            value: parsedRules.event.params.tag || "",
          },
        ],
        createdAt: tagRegex.createdAt,
      };
    });

    setRules(existingRules);
  }, [tagRegexesTransaction, combinationOperator]);

  return (
    <Modal size="lg" show={showModal} onHide={toggle}>
      {/* @ts-ignore - react-bootstrap 1.6.1 type definition issue */}
      <Modal.Header closeButton>
        <Modal.Title>Edit Transaction Tagging Rules</Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{
          height: "35em",
          overflowY: rules.length > 1 ? "scroll" : "hidden",
        }}
      >
        {loading && <ClipLoader />}
        {!loading && (
          <>
            <Row>
              {editRuleIndex === null ? (
                <Col>
                  <Button variant="link" onClick={handleAddRule}>
                    <FontAwesomeIcon icon="plus" />
                    <span className="ml-1">Add new rule</span>
                  </Button>
                </Col>
              ) : (
                <Col>
                  <Button
                    variant="link"
                    disabled={hasError}
                    onClick={() => {
                      if (addingNewRule) {
                        handleOnRemoveRule(rules.length - 1);
                      } else {
                        setEditRuleIndex(null);
                      }
                    }}
                    className="float-right"
                  >
                    View all rules
                  </Button>
                </Col>
              )}
            </Row>
            {editRuleIndex === null ? (
              <>
                <TagRegexesTransactionViewOnly
                  rules={rules}
                  onEdit={setEditRuleIndex}
                  onRemove={handleOnRemoveRule}
                />
              </>
            ) : (
              <Row className="flex-column">
                {ruleToEdit && (
                  <Col style={{ maxHeight: "auto", marginBottom: "0" }}>
                    <Card className="mb-4">
                      <Card.Body>
                        <Row>
                          <FormGroup as={Col} className="d-flex">
                            <Form.Label>If</Form.Label>
                            <Select
                              options={[
                                { value: "all", label: "All" },
                                { value: "any", label: "Any" },
                              ]}
                              value={{
                                value: combinationOperator,
                                label:
                                  combinationOperator === "all" ? "All" : "Any",
                              }}
                              onChange={(option) =>
                                option && setCombinationOperator(option.value)
                              }
                              className="col-4"
                            />
                          </FormGroup>
                          <Col className="text-right">
                            <Button
                              variant="link"
                              onClick={() => handleOnRemoveRule(editRuleIndex)}
                            >
                              <FontAwesomeIcon icon="trash" />
                            </Button>
                          </Col>
                        </Row>
                        {ruleToEdit.ifConditions.map((condition, index) => (
                          <Form key={index}>
                            <Form.Row>
                              <FormGroup as={Col} md="3">
                                <Select
                                  options={availableFields}
                                  value={{
                                    value: condition.field,
                                    label:
                                      availableFields.find(
                                        (f) => f.value === condition.field
                                      )?.label || condition.field,
                                  }}
                                  onChange={(option) => {
                                    if (option) {
                                      handleIfConditionChange(
                                        editRuleIndex,
                                        index,
                                        "field",
                                        option.value
                                      );
                                      handleOnBlurUpdateRule(editRuleIndex);
                                    }
                                  }}
                                />
                              </FormGroup>
                              <FormGroup as={Col} md="3">
                                <Select
                                  options={getOperatorsForField(
                                    condition.field
                                  )}
                                  value={{
                                    value: condition.operator,
                                    label:
                                      getOperatorsForField(
                                        condition.field
                                      ).find(
                                        (op) => op.value === condition.operator
                                      )?.label || condition.operator,
                                  }}
                                  onChange={(option) => {
                                    if (option) {
                                      handleIfConditionChange(
                                        editRuleIndex,
                                        index,
                                        "operator",
                                        option.value
                                      );
                                      handleOnBlurUpdateRule(editRuleIndex);
                                    }
                                  }}
                                />
                              </FormGroup>
                              <FormGroup as={Col} md="5">
                                <Form.Control
                                  name="value"
                                  type={
                                    condition.field === "amount" ||
                                    condition.field === "balance"
                                      ? "number"
                                      : "text"
                                  }
                                  value={
                                    condition.field === "amount" ||
                                    condition.field === "balance"
                                      ? condition.value.amount || ""
                                      : condition.value.keywords
                                      ? condition.value.keywords.join(", ")
                                      : ""
                                  }
                                  onChange={(e) => {
                                    handleIfConditionChange(
                                      editRuleIndex,
                                      index,
                                      "value",
                                      e.target.value
                                    );
                                    setErrorMap({
                                      ...errorMap,
                                      keyword: !!!e.target.value,
                                    });
                                  }}
                                  onBlur={() => {
                                    handleOnBlurUpdateRule(editRuleIndex);
                                  }}
                                />
                                {errorMap.keyword && (
                                  <ErrorMessage>
                                    {condition.field === "amount" ||
                                    condition.field === "balance"
                                      ? "Amount is required"
                                      : "Value is required"}
                                  </ErrorMessage>
                                )}
                              </FormGroup>
                              <FormGroup as={Col} md="1">
                                {index > 0 && (
                                  <Button
                                    variant="link"
                                    onClick={() =>
                                      handleRemoveIfCondition(
                                        editRuleIndex,
                                        index
                                      )
                                    }
                                  >
                                    <FontAwesomeIcon icon="trash" />
                                  </Button>
                                )}
                              </FormGroup>
                            </Form.Row>
                          </Form>
                        ))}
                        <Row>
                          <Col md="12">
                            <Button
                              variant="link"
                              onClick={() =>
                                handleAddIfCondition(editRuleIndex)
                              }
                              disabled={remainingFields.length === 0}
                            >
                              <FontAwesomeIcon icon="plus" />
                              <span className="ml-1">
                                Add another condition
                              </span>
                            </Button>
                          </Col>
                          <Col md="12">
                            <Form.Label>Then set</Form.Label>
                          </Col>
                        </Row>
                        {ruleToEdit.thenSet.map((thenSet, index) => (
                          <Form key={index}>
                            <Form.Row className="align-items-center">
                              <FormGroup as={Col} md="2">
                                <Form.Label>Tag</Form.Label>
                              </FormGroup>
                              <FormGroup
                                as={Col}
                                md="1"
                                className="text-center"
                              >
                                <Form.Label>to</Form.Label>
                              </FormGroup>
                              <FormGroup as={Col} md="8">
                                <Form.Control
                                  type="text"
                                  value={thenSet.value}
                                  placeholder="Enter tag name"
                                  onChange={(e) => {
                                    handleRuleChange(
                                      editRuleIndex,
                                      index,
                                      "value",
                                      e.target.value
                                    );
                                    setErrorMap({
                                      ...errorMap,
                                      tag: !!!e.target.value,
                                    });
                                  }}
                                  onBlur={() => {
                                    handleOnBlurUpdateRule(editRuleIndex);
                                  }}
                                />
                                {errorMap.tag && (
                                  <ErrorMessage>Tag is required</ErrorMessage>
                                )}
                              </FormGroup>
                              <FormGroup as={Col} md="1">
                                {index > 0 && (
                                  <Button
                                    variant="link"
                                    onClick={() =>
                                      handleRemoveThenSet(editRuleIndex, index)
                                    }
                                  >
                                    <FontAwesomeIcon icon="trash" />
                                  </Button>
                                )}
                              </FormGroup>
                            </Form.Row>
                          </Form>
                        ))}
                        <Button
                          variant="link"
                          onClick={() => handleAddThenSet(editRuleIndex)}
                          className="mb-2 ml-2"
                        >
                          <FontAwesomeIcon icon="plus" />
                          <span className="ml-1">Add another action</span>
                        </Button>
                      </Card.Body>
                      {addingNewRule && (
                        <Card.Footer>
                          <LoadingButton
                            isLoading={false}
                            variant="primary"
                            onClick={handleOnCreateRule}
                            className="float-right"
                            disabled={hasError}
                          >
                            <FontAwesomeIcon icon="plus" />
                            <span className="ml-1">Add new rule</span>
                          </LoadingButton>
                        </Card.Footer>
                      )}
                    </Card>
                  </Col>
                )}
              </Row>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={toggle}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TagRegexesTransaction;
